<script setup lang="ts">
import { useAiChat } from "ls/api/cloud/ai";
import { computed, useTemplateRef } from "vue";
import { useChatActionMessagesCollection } from "../disko/rich-text/ai/useChatActionMessagesCollection";
import AiChatTextBox from "./AiChatTextBox.vue";
import AnimatedDots from "./AnimatedDots.vue";
import StreamingMarkdownViewer from "./StreamingMarkdownViewer.vue";
import { useChatAutoScroll } from "./useChatAutoScroll";

const props = defineProps<{
  chatId: string;
}>();

const emit = defineEmits<{
  openDocument: [messageId: string];
  insertMessageId: [messageId: string];
  maximize: [];
}>();

const conversation = useAiChat(props.chatId);

function openDocument(messageId: string | undefined) {
  if (!messageId) return;
  emit("openDocument", messageId);
}

const { actionMessages, lastActionMessage } = useChatActionMessagesCollection(() => props.chatId);

function getActionMessageNumber(messageId: string | undefined) {
  if (!messageId) return 0;
  return actionMessages.value.findIndex(m => m.id === messageId);
}

const isLatestMessagePending = computed(() => {
  const lastMessage = lastActionMessage.value;
  if (!lastMessage) return false;
  return lastMessage.content.length === 0; // && lastMessage.role !== "action";
});

const scrollContainerRef = useTemplateRef("scrollContainerRef");

useChatAutoScroll(scrollContainerRef, () => {
  const lastMessage = conversation.messages.at(-1);
  return lastMessage?.incomingMessageContent || lastMessage?.content;
}, {
  behavior: "smooth",
  deep: true,
});

const messageIndexToDocumentPrompt = computed(() => {
  const tag = "DocumentPrompt";
  const messages = conversation.messages;
  return messages.reduce((acc, message) => {
    const content = message.content;
    const startTagIndex = content?.findIndex(c => c.text.includes(`<${tag}>`));
    const endTagIndex = content?.findIndex(c => c.text.includes(`</${tag}>`));

    if (!message.hidden) {
      acc.push({
        id: message.id,
        role: message.role,
        beforeTag: startTagIndex !== undefined && startTagIndex >= 0 && endTagIndex
          ? content?.slice(0, startTagIndex)
          : content,
        withinTag: startTagIndex !== undefined && startTagIndex >= 0 && endTagIndex
          ? content?.slice(startTagIndex + 1, endTagIndex)
          : [],
        incomingMessageContent: message.incomingMessageContent,
      });
    }
    return acc;
  }, [] as Array<{
    id: string | undefined;
    role: string;
    beforeTag: typeof messages[number]["content"];
    withinTag: typeof messages[number]["content"];
    incomingMessageContent: typeof messages[number]["incomingMessageContent"];
  }>);
});
</script>

<template>
  <div class="flex h-full min-h-0 flex-col gap-2">
    <div
      ref="scrollContainerRef"
      class="max-w-full flex-1 overflow-auto pb-20"
    >
      <template v-if="conversation && conversation.messages.length > 0">
        <div
          v-for="message in messageIndexToDocumentPrompt"
          :key="message.beforeTag.join('') + message.withinTag.join('')"
          class="flex max-w-full"
        >
          <div
            v-if="message.role === 'assistant' && (message.beforeTag.length > 0 || message.withinTag.length > 0 || message.incomingMessageContent)"
            class="flex max-w-full flex-col gap-2"
          >
            <div
              v-if="message.beforeTag.length > 0"
              class="relative mt-4 rounded-md bg-slate-50 px-2 py-2 text-left"
            >
              <StreamingMarkdownViewer
                prose-variant="small"
                :content="message.beforeTag"
                :incoming-message-content="message.incomingMessageContent"
              />
            </div>
            <div
              v-if="message.withinTag.length > 0"
              class="mr-auto ml-2 flex w-[80%] flex-col gap-y-1 rounded-lg border bg-white px-2 py-4 text-left hover:bg-purple-50"
              :class="{ 'mt-4': message.beforeTag.length <= 0 }"
              @click="openDocument(message.id)"
            >
              <div class="font-semibold">
                Version {{ getActionMessageNumber(message.id) + 1 }}
              </div>
              <div v-if="message.withinTag.length > 0" class="truncate text-xs text-gray-500">
                {{ message.withinTag[0]?.text?.slice(0, 100) }}
              </div>
              <AnimatedDots
                v-else
                class="text-xs text-gray-500"
              />
            </div>
          </div>
          <div v-else-if="message.role === 'user'" class="mt-4 ml-auto inline-block max-w-[80%] rounded-md bg-blue-100 px-2 text-left">
            <StreamingMarkdownViewer
              :content="message.beforeTag"
              :incoming-message-content="message.incomingMessageContent"
            />
          </div>
        </div>
        <AnimatedDots
          v-if="isLatestMessagePending"
          class="my-4 ml-2"
        />
      </template>
    </div>
    <div class="flex max-h-1/3 flex-col items-end overflow-hidden">
      <AiChatTextBox
        :chat-id="chatId"
        placeholder="Message"
        @maximize="emit('maximize')"
      />
    </div>
  </div>
</template>

<style scoped>
.prose-sm :where(p) {
  margin-top: 0rem;
}

.prose-sm :where(p):is(:last-child) {
  margin-bottom: 0rem;
}
</style>
